import clsx from 'clsx'

import { Text } from '@wartek-id/text'
import { Separator } from '@wartek-id/separator'
import { Link } from '@wartek-id/link'

import { PanelKinerjaSourceTabId } from 'types/kinerja-ks'

import type {
  KinerjaKsKepemimpinanSatuanPendidikan,
  KinerjaKsRencanaHasilKerjaExtra,
  KinerjaKsUtamaDetail,
} from 'types/kinerja-ks'
import type { HTMLProps } from 'react'

const DEFAULT_LINK_GUIDE = 'https://pusatinformasi.guru.kemdikbud.go.id'

type Kinerja =
  | KinerjaKsUtamaDetail
  | KinerjaKsKepemimpinanSatuanPendidikan
  | KinerjaKsRencanaHasilKerjaExtra
export interface PanelKinerjaProps extends HTMLProps<HTMLDivElement> {
  kinerja?: Kinerja & {
    sourceTabId: PanelKinerjaSourceTabId
  }
  number: number | string
  linkGuide?: string
  className?: string
  hideDetails?: boolean
  label?: string
}

const PanelKinerja = ({
  kinerja,
  number,
  linkGuide = DEFAULT_LINK_GUIDE,
  className = '',
  hideDetails = false,
  label = 'Rencana Hasil Kerja',
  ...otherProps
}: PanelKinerjaProps) => {
  const displayedTitle =
    (kinerja as KinerjaKsUtamaDetail)?.rencanaHasilKerja ||
    (kinerja as KinerjaKsKepemimpinanSatuanPendidikan)?.rencanaHasilKerja ||
    (kinerja as KinerjaKsRencanaHasilKerjaExtra)?.nama ||
    '-'
  const displayedBuktiDukung =
    kinerja?.buktiDukung?.map((b) => b.deskripsi) ?? []
  const displayedTargetKuantitas = kinerja?.targetKuantitas || '-'

  return (
    <div
      className={clsx('bg-white border border-gray-30 rounded p-6', className)}
      data-testid="panel-kinerja"
      {...otherProps}
    >
      <div className="flex items-start gap-5">
        <Text variant="heading-sm" color="primary">
          {number}.
        </Text>
        <div className="flex flex-col gap-1">
          <Text color="subdued">{label}</Text>
          <Text variant="heading-sm" className="!font-normal">
            {displayedTitle}
          </Text>
          {'catatan' in kinerja && kinerja.catatan && (
            <Text className="mt-4">Catatan: {kinerja.catatan}</Text>
          )}
        </div>
      </div>

      {!hideDetails && (
        <>
          <Separator className="my-6" />

          <div className="flex flex-col gap-6">
            <div className="flex gap-6">
              <div className="w-full flex flex-col gap-1">
                <Text color="subdued">Bukti Dukung</Text>
                {!displayedBuktiDukung.length && '-'}
                {displayedBuktiDukung.map((buktiDukung, i, allBuktiDukung) => (
                  <Text key={`buktiDukung.${i}`} className="!font-bold">
                    {allBuktiDukung.length > 1 && `${i + 1}. `}
                    {buktiDukung ?? '-'}
                  </Text>
                ))}
              </div>

              {kinerja.sourceTabId ===
                PanelKinerjaSourceTabId.PengembanganKompetensi && (
                <div className="w-full flex flex-col gap-1">
                  <Text color="subdued">Target Kuantitas</Text>
                  <Text className="!font-bold">{displayedTargetKuantitas}</Text>
                </div>
              )}

              <div className="w-full flex flex-col gap-1">
                <Text color="subdued">Target</Text>
                <Text className="!font-bold">100%</Text>
              </div>

              {!!kinerja && 'bobot' in kinerja && !!kinerja.bobot && (
                <div className="w-full flex flex-col gap-1">
                  <Text color="subdued">Poin</Text>
                  <Text className="!font-bold">{kinerja.bobot}</Text>
                </div>
              )}

              {/* Perspektif */}
              <div className="w-full flex flex-col gap-1">
                <Text color="subdued">Perspektif</Text>
                <Text className="!font-bold">{kinerja?.perspektif ?? '-'}</Text>
              </div>
            </div>
          </div>

          {linkGuide && (
            <div className="mt-1">
              <Link
                target="_blank"
                href={linkGuide}
                data-testid="link-guide"
                className="font-semibold !no-underline text-[13px]"
              >
                Cek panduan
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default PanelKinerja
